import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Badge, Empty, Field, Grid, Note, Panel, Spinner, Stat, TableWrap } from '@wyocspay/ui';
import { dateTime, methodLabel, money } from './format.js';
import {
  ApiError,
  api,
  type FeeAnalyticsBucket,
  type FeeBreakEven,
  type FeeCatalogVersion,
  type FeeEstimate,
  type FeeRecommendation,
  type FeeRule,
  type Plan,
  type PlanAssignment,
} from './api.js';

/**
 * The new fee system, seen from the console.
 *
 * Five separate readers, kept apart on purpose:
 *  - Анализ на таксите: how far the estimate drifted from what Stripe actually
 *    charged, and what the platform's margin is, over a window.
 *  - Калкулатор на тарифи: the same FeeEngine that prices a real charge, run on
 *    a hypothetical. Every figure is rendered from the response - nothing is
 *    computed here, so a quote and a live charge can never disagree.
 *  - Каталог с тарифи: the published and draft rule sets, read-only, with the
 *    two writes the spec allows (publish a draft, roll back to a prior version).
 *  - Ценови планове: the public plans and, for one merchant at a time, which
 *    plan they are on and the option to move them.
 *  - Авариен режим: which platform-wide mode is in force. Read-only here; it is
 *    changed through the signed platform-settings control like every other
 *    setting that moves money.
 */

// ---------------------------------------------------------------------------
// Shared load helper - the same pattern the other read views use.
// ---------------------------------------------------------------------------

function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async (silent?: unknown) => {
    const quiet = silent === true;
    if (!quiet) setLoading(true);
    try {
      setData(await load());
      setError(null);
    } catch (err) {
      if (!quiet) setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      if (!quiet) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void run();
  }, [run]);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void run(true);
    }, 5000);
    return () => clearInterval(id);
  }, [run]);

  return { data, error, loading, reload: run };
}

function Frame({ loading, error, children }: { loading: boolean; error: string | null; children: ReactNode }) {
  if (loading) return <Spinner label="Зареждане…" />;
  if (error) return <Note tone="danger">{error}</Note>;
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Bulgarian wording for the enum values these screens return. Local, like the
// BEARER labels on the Fees page - not every enum belongs in format.ts.
// ---------------------------------------------------------------------------

const CHANNEL_LABEL: Record<string, string> = {
  online: 'онлайн',
  card_present: 'на място',
  payout: 'изплащане',
  unknown: 'неизвестен',
  '*': 'всички',
};

const CARD_CATEGORY_LABEL: Record<string, string> = {
  domestic: 'местна карта',
  premium: 'премиум карта',
  international: 'международна карта',
};

function channelLabel(code: string): string {
  return CHANNEL_LABEL[code] ?? code;
}

/** Currencies the console offers in the calculator - all two-decimal. */
const CURRENCIES = ['usd', 'eur', 'gbp'];

/** The catalogue's known methods, for the calculator's selects. */
const METHODS = [
  'card',
  'link',
  'apple_pay',
  'google_pay',
  'ach_debit',
  'sepa_debit',
  'bacs_debit',
  'pay_by_bank',
  'interac',
  'bank_transfer',
];

/** bps -> "2.90%", the display convention used across the console. */
function pct(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

// ===========================================================================
// 1. Анализ на таксите
// ===========================================================================

const SINCE_PRESETS = [30, 90, 180, 365];

export function FeeAnalyticsView() {
  const [days, setDays] = useState(90);
  const { data, error, loading } = useLoad(() => api.feeAnalytics(days), [days]);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="Оценка срещу реалност, върху уредените плащания">
        Числата идват от заснетите такси на всяко плащане, след като Stripe го е уредил. Отклонението
        е реалната такса минус оценената - положително значи, че сме подценили разхода. Плащане, чиято
        реална такса още не е прочетена, не влиза тук и се брои отделно като „чакащо“.
      </Note>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {SINCE_PRESETS.map((d) => (
          <button
            key={d}
            type="button"
            className={`wc-btn wc-btn-sm ${days === d ? '' : 'wc-btn-secondary'}`}
            onClick={() => setDays(d)}
          >
            {d} дни
          </button>
        ))}
      </div>

      <Frame loading={loading} error={error}>
        {data ? (
          <>
            {data.byCurrency.length > 1 ? (
              <Note tone="warn" title="Няколко валути">
                Обобщените суми по-долу събират различни валути в едно число - вижте таблицата „По
                валута“ за вярната разбивка. Обобщените парични стойности се показват в USD.
              </Note>
            ) : null}

            <Grid cols={3}>
              <Stat
                label="Плащания"
                value={data.totals.count}
                hint={`${data.pendingReconciliation} чакат реална такса`}
                accent="var(--wc-grey)"
              />
              <Stat label="Оборот" value={money(data.totals.grossAmount, 'usd')} />
              <Stat
                label="Марж на платформата"
                value={money(data.totals.platformMargin, 'usd')}
                accent="var(--wc-green)"
              />
            </Grid>

            <Panel title="Оценени срещу реални такси">
              <Grid cols={3}>
                <Stat label="Оценена такса" value={money(data.totals.estimatedTotalFee, 'usd')} accent="var(--wc-grey)" />
                <Stat label="Реална такса" value={money(data.totals.actualTotalFee, 'usd')} accent="var(--wc-blue)" />
                <Stat
                  label="Отклонение (реална − оценена)"
                  value={money(data.totals.estimateDrift, 'usd')}
                  accent={data.totals.estimateDrift > 0 ? 'var(--wc-orange)' : 'var(--wc-blue)'}
                />
              </Grid>
            </Panel>

            <Grid cols={2}>
              <BucketTable title="По метод" buckets={data.byMethod} labelFor={methodLabel} aggregate />
              <BucketTable title="По канал" buckets={data.byChannel} labelFor={channelLabel} aggregate />
            </Grid>
            <Grid cols={2}>
              <BucketTable title="По регион" buckets={data.byRegion} labelFor={(k) => k.toUpperCase()} aggregate />
              <BucketTable
                title="По валута"
                buckets={data.byCurrency}
                labelFor={(k) => k.toUpperCase()}
                aggregate={false}
              />
            </Grid>
          </>
        ) : null}
      </Frame>
    </div>
  );
}

/**
 * One breakdown table. `aggregate` decides the currency the money is shown in:
 * the method/channel/region buckets mix currencies and are shown in USD; the
 * per-currency table shows each row in its own currency, where the key is it.
 */
function BucketTable({
  title,
  buckets,
  labelFor,
  aggregate,
}: {
  title: string;
  buckets: FeeAnalyticsBucket[];
  labelFor: (key: string) => string;
  aggregate: boolean;
}) {
  const maxGross = Math.max(1, ...buckets.map((b) => b.grossAmount));
  return (
    <Panel title={title} padded={false}>
      {buckets.length > 0 ? (
        <TableWrap>
          <table className="wc-table">
            <thead>
              <tr>
                <th>{title.replace('По ', '').replace(/^./, (c) => c.toUpperCase())}</th>
                <th className="wc-right">Брой</th>
                <th className="wc-right">Оборот</th>
                <th className="wc-right">Реална такса</th>
                <th className="wc-right">Отклонение</th>
                <th className="wc-right">Марж</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b) => {
                const ccy = aggregate ? 'usd' : b.key;
                return (
                  <tr key={b.key}>
                    <td>{labelFor(b.key)}</td>
                    <td className="wc-right wc-num">{b.count}</td>
                    <td className="wc-right wc-num">
                      <div style={{ display: 'grid', gap: 3, justifyItems: 'end' }}>
                        <span>{money(b.grossAmount, ccy)}</span>
                        <span
                          aria-hidden
                          style={{
                            display: 'block',
                            width: `${Math.round((b.grossAmount / maxGross) * 100)}%`,
                            minWidth: 2,
                            height: 3,
                            background: 'var(--wc-blue)',
                            borderRadius: 2,
                          }}
                        />
                      </div>
                    </td>
                    <td className="wc-right wc-num">{money(b.actualTotalFee, ccy)}</td>
                    <td
                      className="wc-right wc-num"
                      style={{ color: b.estimateDrift > 0 ? 'var(--wc-orange)' : 'var(--wc-muted)' }}
                    >
                      {money(b.estimateDrift, ccy)}
                    </td>
                    <td className="wc-right wc-num" style={{ fontWeight: 600, color: 'var(--wc-green)' }}>
                      {money(b.platformMargin, ccy)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      ) : (
        <Empty>Няма уредени плащания за този период.</Empty>
      )}
    </Panel>
  );
}

// ===========================================================================
// 2. Калкулатор на тарифи
// ===========================================================================

export function TariffCalculatorView() {
  const [amount, setAmount] = useState('50.00');
  const [currency, setCurrency] = useState('usd');
  const [country, setCountry] = useState('US');
  const [channel, setChannel] = useState<'online' | 'card_present'>('online');
  const [method, setMethod] = useState('card');
  const [cardCategory, setCardCategory] = useState<'domestic' | 'premium' | 'international'>('domestic');
  const [appBps, setAppBps] = useState('');
  const [appFixed, setAppFixed] = useState('');
  const [methods, setMethods] = useState<string[]>(['card', 'apple_pay', 'ach_debit']);
  const [target, setTarget] = useState('70');

  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<FeeEstimate | null>(null);
  const [compare, setCompare] = useState<Array<{ method: string; estimate: FeeEstimate }> | null>(null);
  const [recommend, setRecommend] = useState<FeeRecommendation | null>(null);
  const [breakEven, setBreakEven] = useState<FeeBreakEven | null>(null);

  const amountMinor = Math.round((parseFloat(amount) || 0) * 100);
  const appBpsVal = appBps.trim() === '' ? null : Number.parseInt(appBps, 10);
  const appFixedVal = appFixed.trim() === '' ? null : Number.parseInt(appFixed, 10);
  const shared = {
    currency,
    country: country.trim() || null,
    channel,
    applicationFeeBps: appBpsVal,
    applicationFeeFixed: appFixedVal,
  };

  async function run(key: string, work: () => Promise<void>): Promise<void> {
    setBusy(key);
    setError(null);
    try {
      await work();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  function toggleMethod(m: string): void {
    setMethods((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  }

  const busyAny = busy !== null;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="Пресмята сървърът, не тази страница">
        Всяко число тук идва от същия механизъм, който таксува реалното плащане. Страницата само
        подава входа и показва отговора - не смята нищо сама, за да не може оценка тук да се разминава
        с истинската такса.
      </Note>

      <Panel title="Вход">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14 }}>
          <Field label={`Сума (${currency.toUpperCase()})`} hint="В основната единица, напр. 50.00">
            <input className="wc-input wc-num" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="Валута">
            <select className="wc-input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c.toUpperCase()}</option>
              ))}
            </select>
          </Field>
          <Field label="Държава" hint="Двубуквен код, решава региона">
            <input className="wc-input" value={country} maxLength={4} onChange={(e) => setCountry(e.target.value.toUpperCase())} />
          </Field>
          <Field label="Канал">
            <select className="wc-input" value={channel} onChange={(e) => setChannel(e.target.value as 'online' | 'card_present')}>
              <option value="online">{channelLabel('online')}</option>
              <option value="card_present">{channelLabel('card_present')}</option>
            </select>
          </Field>
          <Field label="Метод">
            <select className="wc-input" value={method} onChange={(e) => setMethod(e.target.value)}>
              {METHODS.map((m) => (
                <option key={m} value={m}>{methodLabel(m)}</option>
              ))}
            </select>
          </Field>
          <Field label="Вид карта">
            <select
              className="wc-input"
              value={cardCategory}
              onChange={(e) => setCardCategory(e.target.value as 'domestic' | 'premium' | 'international')}
            >
              <option value="domestic">{CARD_CATEGORY_LABEL.domestic}</option>
              <option value="premium">{CARD_CATEGORY_LABEL.premium}</option>
              <option value="international">{CARD_CATEGORY_LABEL.international}</option>
            </select>
          </Field>
          <Field label="Такса на платформата, %" hint="Базисни точки, напр. 290 = 2.90%. Празно = по подразбиране">
            <input className="wc-input wc-num" inputMode="numeric" value={appBps} onChange={(e) => setAppBps(e.target.value)} />
          </Field>
          <Field label="Такса на платформата, фикс" hint="В стотинки, напр. 30 = $0.30. Празно = по подразбиране">
            <input className="wc-input wc-num" inputMode="numeric" value={appFixed} onChange={(e) => setAppFixed(e.target.value)} />
          </Field>
        </div>

        <div style={{ marginTop: 8 }}>
          <span className="wc-label">Методи за сравнение</span>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
            {METHODS.map((m) => (
              <label key={m} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13.5 }}>
                <input type="checkbox" checked={methods.includes(m)} onChange={() => toggleMethod(m)} />
                {methodLabel(m)}
              </label>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14, alignItems: 'end' }}>
          <button
            type="button"
            className="wc-btn"
            disabled={busyAny}
            onClick={() => void run('estimate', async () => {
              setEstimate(await api.feeEstimate({ ...shared, amount: amountMinor, method, cardCategory }));
            })}
          >
            {busy === 'estimate' ? 'Смята се…' : 'Оцени'}
          </button>
          <button
            type="button"
            className="wc-btn wc-btn-secondary"
            disabled={busyAny || methods.length === 0}
            onClick={() => void run('compare', async () => {
              const r = await api.feeCompare({ ...shared, amount: amountMinor, methods });
              setCompare(r.results);
            })}
          >
            {busy === 'compare' ? 'Смята се…' : 'Сравни методи'}
          </button>
          <button
            type="button"
            className="wc-btn wc-btn-secondary"
            disabled={busyAny || methods.length === 0}
            onClick={() => void run('recommend', async () => {
              setRecommend(await api.feeRecommend({ ...shared, amount: amountMinor, methods }));
            })}
          >
            {busy === 'recommend' ? 'Смята се…' : 'Препоръчай метод'}
          </button>
          <div style={{ display: 'flex', gap: 8, alignItems: 'end', marginLeft: 'auto' }}>
            <label className="wc-field" style={{ marginBottom: 0, width: 130 }}>
              <span className="wc-label">Целеви нетен %</span>
              <input className="wc-input wc-num" inputMode="numeric" value={target} onChange={(e) => setTarget(e.target.value)} />
            </label>
            <button
              type="button"
              className="wc-btn wc-btn-secondary"
              disabled={busyAny}
              onClick={() => void run('breakEven', async () => {
                const frac = (Number.parseFloat(target) || 0) / 100;
                setBreakEven(await api.feeBreakEven({ ...shared, method, cardCategory, targetNetFraction: frac }));
              })}
            >
              {busy === 'breakEven' ? 'Смята се…' : 'Праг на рентабилност'}
            </button>
          </div>
        </div>
      </Panel>

      {error ? <Note tone="danger">{error}</Note> : null}

      {estimate ? <EstimateResult estimate={estimate} /> : null}
      {compare ? <CompareResult results={compare} /> : null}
      {recommend ? <RecommendResult rec={recommend} /> : null}
      {breakEven ? <BreakEvenResult data={breakEven} /> : null}
    </div>
  );
}

function PricedBadge({ priced }: { priced: boolean }) {
  return priced ? (
    <Badge tone="ok" icon={false}>ОТ КАТАЛОГА</Badge>
  ) : (
    <Badge tone="warn" icon={false}>БЕЗ ПРАВИЛО</Badge>
  );
}

function EstimateResult({ estimate: e }: { estimate: FeeEstimate }) {
  return (
    <Panel
      title="Оценка"
      actions={<PricedBadge priced={e.priced} />}
    >
      <Grid cols={4}>
        <Stat label="Сума" value={money(e.gross, e.currency)} accent="var(--wc-grey)" />
        <Stat label="Такса на обработващия" value={money(e.processorFee, e.currency)} accent="var(--wc-blue)" />
        <Stat label="Такса на платформата" value={money(e.applicationFee, e.currency)} accent="var(--wc-green)" />
        <Stat label="Нето за търговеца" value={money(e.merchantNet, e.currency)} />
      </Grid>

      {e.components.length > 0 ? (
        <TableWrap>
          <table className="wc-table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Компонент</th>
                <th>Детайл</th>
                <th className="wc-right">Сума</th>
              </tr>
            </thead>
            <tbody>
              {e.components.map((c) => (
                <tr key={c.key}>
                  <td>{c.label}</td>
                  <td style={{ color: 'var(--wc-muted)', fontSize: 12.5 }}>{c.detail ?? '-'}</td>
                  <td className="wc-right wc-num">{money(c.amount, e.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      ) : null}

      {e.note ? <Note tone="warn" title="Бележка от механизма">{e.note}</Note> : null}
      <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--wc-faint)' }}>
        Версия на каталога: <span className="wc-num">{e.catalogVersionId ?? '-'}</span> · Правило:{' '}
        <span className="wc-num">{e.ruleId ?? '-'}</span>
      </p>
    </Panel>
  );
}

function CompareResult({ results }: { results: Array<{ method: string; estimate: FeeEstimate }> }) {
  return (
    <Panel title="Сравнение на методите" padded={false}>
      <p style={{ margin: '12px 16px 0', fontSize: 12.5, color: 'var(--wc-muted)' }}>
        Подредени от най-евтин към най-скъп, както ги връща механизмът.
      </p>
      <TableWrap>
        <table className="wc-table">
          <thead>
            <tr>
              <th>Метод</th>
              <th className="wc-right">Обработващ</th>
              <th className="wc-right">Платформа</th>
              <th className="wc-right">Нето за търговеца</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {results.map((r, i) => (
              <tr key={r.method}>
                <td>
                  {methodLabel(r.method)} {i === 0 ? <Badge tone="ok" icon={false}>НАЙ-ЕВТИН</Badge> : null}
                </td>
                <td className="wc-right wc-num">{money(r.estimate.processorFee, r.estimate.currency)}</td>
                <td className="wc-right wc-num">{money(r.estimate.applicationFee, r.estimate.currency)}</td>
                <td className="wc-right wc-num" style={{ fontWeight: 600 }}>
                  {money(r.estimate.merchantNet, r.estimate.currency)}
                </td>
                <td className="wc-right"><PricedBadge priced={r.estimate.priced} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
    </Panel>
  );
}

function RecommendResult({ rec }: { rec: FeeRecommendation }) {
  return (
    <Panel title="Препоръка">
      <Note tone={rec.savingsVsCard && rec.savingsVsCard > 0 ? 'ok' : 'info'} title={rec.reason}>
        {rec.recommended ? (
          <>
            Препоръчан метод: <strong>{methodLabel(rec.recommended)}</strong>
            {rec.recommendedFee != null ? <> · такса {money(rec.recommendedFee, rec.currency)}</> : null}
            {rec.savingsVsCard != null && rec.savingsVsCard > 0 ? (
              <> · спестява {money(rec.savingsVsCard, rec.currency)} спрямо карта</>
            ) : null}
          </>
        ) : (
          'Няма препоръчан метод.'
        )}
      </Note>

      {rec.ranked.length > 0 ? (
        <TableWrap>
          <table className="wc-table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Метод</th>
                <th className="wc-right">Обща такса</th>
                <th className="wc-right">Нето за търговеца</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rec.ranked.map((r) => (
                <tr key={r.method}>
                  <td>{methodLabel(r.method)}</td>
                  <td className="wc-right wc-num">{money(r.totalFee, rec.currency)}</td>
                  <td className="wc-right wc-num">{money(r.merchantNet, rec.currency)}</td>
                  <td className="wc-right"><PricedBadge priced={r.priced} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      ) : null}
    </Panel>
  );
}

function BreakEvenResult({ data }: { data: FeeBreakEven }) {
  return (
    <Panel title="Праг на рентабилност">
      {data.breakEvenAmount != null ? (
        <Stat
          label={`Сума, при която търговецът задържа ${Math.round(data.targetNetFraction * 100)}%`}
          value={money(data.breakEvenAmount, data.currency)}
          accent="var(--wc-green)"
        />
      ) : (
        <Note tone="warn" title="Няма праг">{data.note ?? 'Тази цел не е постижима при тази тарифа.'}</Note>
      )}
      {data.breakEvenAmount != null && data.note ? <Note tone="warn">{data.note}</Note> : null}
    </Panel>
  );
}

// ===========================================================================
// 3. Каталог с тарифи
// ===========================================================================

const CATALOG_STATUS_TONE: Record<string, 'ok' | 'neutral' | 'info'> = {
  PUBLISHED: 'ok',
  DRAFT: 'info',
  EXPIRED: 'neutral',
};

export function FeeCatalogView() {
  const { data, error, loading, reload } = useLoad(async () => {
    const [versions, active] = await Promise.all([api.feeCatalogVersions(), api.feeCatalogActive()]);
    return { versions: versions.versions, active };
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function act(kind: 'publish' | 'rollback', v: FeeCatalogVersion): Promise<void> {
    const question =
      kind === 'publish'
        ? `Да се публикува ли „${v.label}“? Тази версия става активната тарифа за всяко следващо плащане.`
        : `Да се върне ли към „${v.label}“? Създава се публикувано копие, което става активната тарифа.`;
    if (!window.confirm(question)) return;
    setBusy(v.id);
    setMsg(null);
    try {
      if (kind === 'publish') await api.publishFeeCatalogVersion(v.id);
      else await api.rollbackFeeCatalogVersion(v.id);
      setMsg(kind === 'publish' ? 'Версията е публикувана.' : 'Върнато към избраната версия.');
      await reload();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="Публикуваната версия е неизменна">
        Тарифите живеят във версии. За да се сменят ставки, се публикува нова версия - съществуваща
        публикувана версия не се пренаписва, за да не се промени с обратна сила числото, спрямо което
        е оценено минало плащане.
      </Note>

      {msg ? <Note tone="warn">{msg}</Note> : null}

      <Frame loading={loading} error={error}>
        {data ? (
          <>
            <Panel title="Версии" padded={false}>
              {data.versions.length > 0 ? (
                <TableWrap>
                  <table className="wc-table">
                    <thead>
                      <tr>
                        <th>Версия</th>
                        <th>Състояние</th>
                        <th className="wc-right">Правила</th>
                        <th>В сила от</th>
                        <th>Публикувана</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {data.versions.map((v) => (
                        <tr key={v.id}>
                          <td>
                            <strong>{v.label}</strong>
                            {v.note ? <div style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>{v.note}</div> : null}
                          </td>
                          <td>
                            <Badge tone={CATALOG_STATUS_TONE[v.status] ?? 'neutral'} icon={false}>{v.status}</Badge>
                          </td>
                          <td className="wc-right wc-num">{v.rules}</td>
                          <td style={{ fontSize: 12.5 }}>{dateTime(v.effective_from)}</td>
                          <td style={{ fontSize: 12.5 }}>{v.published_at ? dateTime(v.published_at) : '-'}</td>
                          <td className="wc-right" style={{ whiteSpace: 'nowrap' }}>
                            {v.status === 'DRAFT' ? (
                              <button
                                type="button"
                                className="wc-btn wc-btn-sm"
                                disabled={busy !== null}
                                onClick={() => void act('publish', v)}
                              >
                                {busy === v.id ? '…' : 'Публикувай'}
                              </button>
                            ) : v.status === 'EXPIRED' ? (
                              <button
                                type="button"
                                className="wc-btn wc-btn-sm wc-btn-secondary"
                                disabled={busy !== null}
                                onClick={() => void act('rollback', v)}
                              >
                                {busy === v.id ? '…' : 'Върни към тази'}
                              </button>
                            ) : (
                              <Badge tone="ok" icon={false}>АКТИВНА</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableWrap>
              ) : (
                <Empty>Няма версии на каталога.</Empty>
              )}
            </Panel>

            <Panel
              title={data.active.active ? `Правила на активната версия · ${data.active.active.label}` : 'Активна версия'}
              padded={false}
            >
              {data.active.active ? (
                data.active.rules.length > 0 ? (
                  <RulesTable rules={data.active.rules} />
                ) : (
                  <Empty>Активната версия няма правила.</Empty>
                )
              ) : (
                <Empty>Няма публикувана активна версия.</Empty>
              )}
            </Panel>
          </>
        ) : null}
      </Frame>
    </div>
  );
}

function RulesTable({ rules }: { rules: FeeRule[] }) {
  return (
    <TableWrap>
      <table className="wc-table">
        <thead>
          <tr>
            <th>Регион</th>
            <th>Канал</th>
            <th>Метод</th>
            <th>Вид карта</th>
            <th>Валута</th>
            <th className="wc-right">Процент</th>
            <th className="wc-right">Фикс</th>
            <th className="wc-right">Авт.</th>
            <th className="wc-right">Спор</th>
          </tr>
        </thead>
        <tbody>
          {rules.map((r) => (
            <tr key={r.id}>
              <td className="wc-num">{r.region}</td>
              <td>{channelLabel(r.channel)}</td>
              <td>{r.method === '*' ? 'всички' : methodLabel(r.method)}</td>
              <td style={{ fontSize: 12.5 }}>{r.cardCategory ? CARD_CATEGORY_LABEL[r.cardCategory] ?? r.cardCategory : '-'}</td>
              <td className="wc-num">{r.currency ? r.currency.toUpperCase() : 'всяка'}</td>
              <td className="wc-right wc-num">{pct(r.percentBps)}</td>
              <td className="wc-right wc-num">{r.fixed > 0 ? money(r.fixed, r.currency ?? 'usd') : '-'}</td>
              <td className="wc-right wc-num">{r.authorizationFee > 0 ? money(r.authorizationFee, r.currency ?? 'usd') : '-'}</td>
              <td className="wc-right wc-num">{r.disputeFee > 0 ? money(r.disputeFee, r.currency ?? 'usd') : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

// ===========================================================================
// 4. Ценови планове
// ===========================================================================

export function PlansView() {
  const { data, error, loading } = useLoad(() => api.plans());

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="Планът задава базовата такса">
        Планът казва каква е таксата на платформата за търговеца. Пакетите за освобождаване от такси
        (специален / vipplus) са отделни: те спират таксата, докато са активни, а планът е числото, към
        което тя се връща след това. Затова назначаването на план на търговец с активен пакет не
        променя живата (нулева) такса - само какво ще възстанови пакетът.
      </Note>

      <Frame loading={loading} error={error}>
        {data ? (
          <>
            <Panel title="Планове" padded={false}>
              {data.plans.length > 0 ? (
                <TableWrap>
                  <table className="wc-table">
                    <thead>
                      <tr>
                        <th>План</th>
                        <th>Видимост</th>
                        <th className="wc-right">Месечна такса</th>
                        <th className="wc-right">Такса</th>
                        <th className="wc-right">Вкл. оборот</th>
                        <th>Състояние</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.plans.map((p) => (
                        <PlanRow key={p.id} plan={p} />
                      ))}
                    </tbody>
                  </table>
                </TableWrap>
              ) : (
                <Empty>Няма планове.</Empty>
              )}
            </Panel>

            <MerchantPlanPanel plans={data.plans} />
          </>
        ) : null}
      </Frame>
    </div>
  );
}

function PlanRow({ plan }: { plan: Plan }) {
  const v = plan.active_version;
  return (
    <tr>
      <td>
        <strong>{plan.name}</strong>
        <div style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>{plan.description}</div>
      </td>
      <td>
        {plan.is_public ? (
          <Badge tone="info" icon={false}>ПУБЛИЧЕН</Badge>
        ) : (
          <Badge tone="neutral" icon={false}>СКРИТ</Badge>
        )}
      </td>
      <td className="wc-right wc-num">{v ? money(v.monthly_fee, v.currency) : '-'}</td>
      <td className="wc-right wc-num">
        {v ? (
          v.zero_platform_fee ? (
            <Badge tone="ok" icon={false}>БЕЗ ТАКСА</Badge>
          ) : (
            <>
              {pct(v.fee_percent_bps)}
              {v.fee_fixed > 0 ? ` + ${money(v.fee_fixed, v.currency)}` : ''}
            </>
          )
        ) : (
          '-'
        )}
      </td>
      <td className="wc-right wc-num">{v && v.included_volume != null ? money(v.included_volume, v.currency) : '-'}</td>
      <td>
        <Badge tone={plan.status === 'ACTIVE' ? 'ok' : 'neutral'} icon={false}>{plan.status}</Badge>
      </td>
    </tr>
  );
}

/** Look up one merchant's current plan and, optionally, move them to another. */
function MerchantPlanPanel({ plans }: { plans: Plan[] }) {
  const [merchantId, setMerchantId] = useState('');
  const [lookupId, setLookupId] = useState('');
  const [assignment, setAssignment] = useState<PlanAssignment | null>(null);
  const [looked, setLooked] = useState(false);
  const [planId, setPlanId] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function lookup(): Promise<void> {
    const id = merchantId.trim();
    if (!id) return;
    setBusy('lookup');
    setError(null);
    setMsg(null);
    try {
      const r = await api.merchantPlan(id);
      setAssignment(r.assignment);
      setLookupId(id);
      setLooked(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setAssignment(null);
      setLooked(false);
    } finally {
      setBusy(null);
    }
  }

  async function assign(): Promise<void> {
    if (!lookupId || !planId) return;
    const plan = plans.find((p) => p.id === planId);
    if (!window.confirm(`Да се назначи ли план „${plan?.name ?? planId}“ на този търговец?`)) return;
    setBusy('assign');
    setError(null);
    setMsg(null);
    try {
      const r = await api.assignMerchantPlan(lookupId, planId);
      setMsg(r.feeApplied ? 'Планът е назначен и таксата е приложена.' : 'Планът е назначен. Живата такса не е променена (активен пакет).');
      const refreshed = await api.merchantPlan(lookupId);
      setAssignment(refreshed.assignment);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Panel title="План на търговец">
      <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}>
        <Field label="ID на търговеца" hint="Отваря се и от списъка с търговци">
          <input
            className="wc-input wc-num"
            value={merchantId}
            placeholder="mrc_…"
            onChange={(e) => setMerchantId(e.target.value)}
            style={{ minWidth: 260 }}
          />
        </Field>
        <div style={{ marginBottom: 16 }}>
          <button type="button" className="wc-btn" disabled={busy !== null || !merchantId.trim()} onClick={() => void lookup()}>
            {busy === 'lookup' ? 'Търси се…' : 'Покажи'}
          </button>
        </div>
      </div>

      {error ? <Note tone="danger">{error}</Note> : null}
      {msg ? <Note tone="ok">{msg}</Note> : null}

      {looked ? (
        assignment ? (
          <div style={{ display: 'grid', gap: 4, marginTop: 4 }}>
            <div style={{ fontSize: 14 }}>
              Текущ план: <strong>{assignment.plan.name}</strong>{' '}
              <span className="wc-num" style={{ color: 'var(--wc-muted)' }}>
                {assignment.version.zero_platform_fee
                  ? '(без такса)'
                  : `(${pct(assignment.version.fee_percent_bps)}${assignment.version.fee_fixed > 0 ? ` + ${money(assignment.version.fee_fixed, 'usd')}` : ''})`}
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>От {dateTime(assignment.started_at)}</div>
          </div>
        ) : (
          <Note>Този търговец не е на публичен план.</Note>
        )
      ) : null}

      {looked ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap', marginTop: 12 }}>
          <Field label="Назначи план">
            <select className="wc-input" value={planId} onChange={(e) => setPlanId(e.target.value)} style={{ minWidth: 240 }}>
              <option value="">Избери план…</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </Field>
          <div style={{ marginBottom: 16 }}>
            <button type="button" className="wc-btn wc-btn-secondary" disabled={busy !== null || !planId} onClick={() => void assign()}>
              {busy === 'assign' ? 'Назначава се…' : 'Назначи'}
            </button>
          </div>
        </div>
      ) : null}

      <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--wc-faint)' }}>
        Назначаването изисква администраторска роля. При активен пакет за освобождаване планът задава
        само числото, към което таксата ще се върне.
      </p>
    </Panel>
  );
}

// ===========================================================================
// 5. Авариен режим
// ===========================================================================

const EMERGENCY_LABEL: Record<string, { label: string; detail: string }> = {
  NORMAL: { label: 'Нормален режим', detail: 'Всичко работи както обикновено.' },
  PAYMENTS_PAUSED: { label: 'Плащанията са спрени', detail: 'Не може да започне ново плащане по никой канал.' },
  ONLINE_ONLY: { label: 'Само онлайн', detail: 'Приемат се само онлайн плащания; плащанията на място са спрени.' },
  IN_PERSON_ONLY: { label: 'Само на място', detail: 'Приемат се само плащания на място; онлайн е спрян.' },
  BANK_ONLY: { label: 'Само банкови', detail: 'Приемат се само банкови методи.' },
  INVOICE_ONLY: { label: 'Само фактури', detail: 'Приемат се само плащания по фактура.' },
  ONBOARDING_PAUSED: { label: 'Регистрацията е спряна', detail: 'Не се създават нови профили на търговци.' },
  PAYOUTS_PAUSED: { label: 'Изплащанията са спрени', detail: 'Средствата не напускат платформата.' },
  INSTANT_PAYOUTS_PAUSED: { label: 'Бързите изплащания са спрени', detail: 'Само стандартни изплащания.' },
  PLATFORM_FEES_PAUSED: { label: 'Таксите на платформата са спрени', detail: 'Платформата не удържа такса от плащанията.' },
  MAINTENANCE: { label: 'Поддръжка', detail: 'Платформата е в режим на поддръжка.' },
};

function emergencyLabel(mode: string): { label: string; detail: string } {
  return EMERGENCY_LABEL[mode] ?? { label: mode, detail: '' };
}

export function EmergencyModeView() {
  const { data, error, loading } = useLoad(() => api.emergencyMode());

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="Само за четене тук">
        Аварийният режим се сменя през подписаната команда в „Настройки на платформата“ - като всяка
        друга настройка, която движи пари. Тази страница показва кой режим е в сила и кои са
        възможностите.
      </Note>

      <Frame loading={loading} error={error}>
        {data ? (
          <>
            <Panel title="Текущ режим">
              <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
                <Badge tone={data.current === 'NORMAL' ? 'ok' : 'warn'}>{emergencyLabel(data.current).label}</Badge>
                <span title={data.current} className="wc-num" style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>
                  {data.current}
                </span>
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--wc-muted)' }}>
                {emergencyLabel(data.current).detail}
              </p>
            </Panel>

            <Panel title="Възможни режими" padded={false}>
              <TableWrap>
                <table className="wc-table">
                  <thead>
                    <tr>
                      <th>Режим</th>
                      <th>Какво прави</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.options.map((mode) => {
                      const info = emergencyLabel(mode);
                      const active = mode === data.current;
                      return (
                        <tr key={mode} style={active ? { background: 'var(--wc-blue-tint)' } : undefined}>
                          <td>
                            <strong>{info.label}</strong>
                            <div className="wc-num" style={{ fontSize: 11.5, color: 'var(--wc-faint)' }}>{mode}</div>
                          </td>
                          <td style={{ fontSize: 13 }}>{info.detail}</td>
                          <td className="wc-right">
                            {active ? <Badge tone="ok" icon={false}>В СИЛА</Badge> : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableWrap>
            </Panel>
          </>
        ) : null}
      </Frame>
    </div>
  );
}
